import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useLang } from "@/i18n/LanguageContext";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Download } from "lucide-react";
import { guideUrl } from "@/lib/api";

const TOTAL = 19;
const PDF_URL = () => guideUrl("Maqam-Digital-Umrah-Guide.pdf");
const pageSrc = (i) => guideUrl(`pages/p${i + 1}.jpg`);
const ASPECT = 1020 / 1814; // width / height of rendered pages
const ZOOMS = [1, 1.75, 2.5];

export const GuideViewer = ({ open, initialPage = 0, onClose }) => {
  const { t } = useLang();
  const [idx, setIdx] = useState(initialPage);
  const [zoomStep, setZoomStep] = useState(0);
  const [box, setBox] = useState({ w: 0, h: 0 });
  const areaRef = useRef(null);
  const scrollRef = useRef(null);
  const touch = useRef({ x: 0, y: 0 });
  const zoom = ZOOMS[zoomStep];

  useEffect(() => {
    if (open) {
      setIdx(initialPage);
      setZoomStep(0);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => { document.body.style.overflow = ""; };
  }, [open, initialPage]);

  // measure available area so a full page always fits (fit-to-page)
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => {
      if (areaRef.current) {
        const r = areaRef.current.getBoundingClientRect();
        setBox({ w: r.width, h: r.height });
      }
    };
    measure();
    window.addEventListener("resize", measure);
    window.addEventListener("orientationchange", measure);
    return () => {
      window.removeEventListener("resize", measure);
      window.removeEventListener("orientationchange", measure);
    };
  }, [open]);

  useEffect(() => {
    [idx - 1, idx + 1].forEach((n) => {
      if (n >= 0 && n < TOTAL) { const im = new Image(); im.src = pageSrc(n); }
    });
  }, [idx]);

  if (!open) return null;

  // contain-fit the page within the measured area, then apply zoom
  const pad = 12;
  const availW = Math.max(0, box.w - pad * 2);
  const availH = Math.max(0, box.h - pad * 2);
  let baseW = availW;
  let baseH = availW / ASPECT;
  if (baseH > availH) { baseH = availH; baseW = availH * ASPECT; }
  const dispW = baseW * zoom;
  const dispH = baseH * zoom;

  const go = (delta) => {
    setZoomStep(0);
    setIdx((i) => Math.min(TOTAL - 1, Math.max(0, i + delta)));
    if (scrollRef.current) scrollRef.current.scrollTo({ top: 0, left: 0 });
  };
  const onTouchStart = (e) => { if (zoom !== 1) return; touch.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; };
  const onTouchEnd = (e) => {
    if (zoom !== 1) return;
    const dx = e.changedTouches[0].clientX - touch.current.x;
    const dy = e.changedTouches[0].clientY - touch.current.y;
    if (Math.abs(dx) > 55 && Math.abs(dx) > Math.abs(dy)) go(dx < 0 ? 1 : -1);
  };
  const toggleZoom = () => setZoomStep((z) => (z + 1) % ZOOMS.length);

  return createPortal(
    <div className="fixed inset-0 z-[90] flex flex-col overflow-hidden bg-[#0A192F]" data-testid="guide-viewer">
      {/* top bar */}
      <div className="flex shrink-0 items-center justify-between gap-2 px-3 py-2.5 pt-[calc(env(safe-area-inset-top)+0.5rem)]">
        <button
          onClick={onClose}
          data-testid="guide-viewer-close"
          className="inline-flex shrink-0 items-center gap-1 rounded-full bg-white/10 py-2 pl-2 pr-3 text-sm font-semibold text-white transition-colors hover:bg-white/20 active:scale-95"
        >
          <ChevronLeft size={18} /> {t("back")}
        </button>
        <span className="truncate text-sm font-semibold text-white/90" data-testid="guide-viewer-page">
          {t("guide.page")} {idx + 1} {t("guide.of")} {TOTAL}
        </span>
        <div className="flex shrink-0 items-center gap-1.5">
          <button onClick={toggleZoom} data-testid="guide-viewer-zoom" className="grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 active:scale-95">
            {zoom === 1 ? <ZoomIn size={17} /> : <ZoomOut size={17} />}
          </button>
          <a href={PDF_URL()} download data-testid="guide-viewer-download" className="grid h-9 w-9 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-white/20 active:scale-95">
            <Download size={16} />
          </a>
        </div>
      </div>

      {/* page area (measured) */}
      <div
        ref={(el) => { areaRef.current = el; scrollRef.current = el; }}
        className={`min-h-0 flex-1 ${zoom === 1 ? "overflow-hidden" : "overflow-auto"}`}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <div className="flex min-h-full w-max min-w-full items-center justify-center" style={{ padding: pad }}>
          {box.w > 0 && (
            <img
              src={pageSrc(idx)}
              alt={`Guide page ${idx + 1}`}
              onDoubleClick={toggleZoom}
              style={{ width: dispW, height: dispH }}
              className="select-none rounded-lg shadow-2xl"
              draggable={false}
              data-testid="guide-viewer-image"
            />
          )}
        </div>
      </div>

      {/* bottom nav */}
      <div className="flex shrink-0 items-center gap-2 px-3 py-2.5 pb-[calc(env(safe-area-inset-bottom)+0.6rem)]">
        <button
          onClick={() => go(-1)}
          disabled={idx === 0}
          data-testid="guide-viewer-prev"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white text-[#0A192F] transition-all disabled:opacity-30 active:scale-95"
        >
          <ChevronLeft size={18} />
        </button>
        <div className="flex min-w-0 flex-1 flex-wrap items-center justify-center gap-1.5">
          {Array.from({ length: TOTAL }).map((_, i) => (
            <button key={i} onClick={() => go(i - idx)} aria-label={`Page ${i + 1}`} className={`h-1.5 rounded-full transition-all ${i === idx ? "w-5 bg-[#E9CE7D]" : "w-1.5 bg-white/30"}`} />
          ))}
        </div>
        <button
          onClick={() => go(1)}
          disabled={idx === TOTAL - 1}
          data-testid="guide-viewer-next"
          className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-white text-[#0A192F] transition-all disabled:opacity-30 active:scale-95"
        >
          <ChevronRight size={18} />
        </button>
      </div>
    </div>,
    document.body
  );
};
